import { type ComponentProps } from 'react';
import { Icon, Severity, Tag, Verdict as VerdictComponent } from '@opswat/ui';
import {
    BadgeProps,
    GetMultiscanVerdict,
    GetSandboxVerdict,
    GetSanitizedVerdict,
    GetScanVerdict,
    SandboxBadgeProps,
    Sanitized,
    ScanResults,
    StatusVerdict,
    TagWithVerdict,
    VerdictObject,
    VerdictTag,
} from 'services/interface';
import { AVResult, CDRResult, ScanResultCode } from 'types/Workflows';

// MDaaS aggregate 253 "Exceeded Limits" is not a Core code, so it stays out of ScanResultCode (which feeds
// the workflow allow-list options): the platform hit a tier limit such as the max archive file number and
// stopped, after the engines had already scanned what it extracted (MDCI-1027).
export const EXCEEDED_LIMITS_CODE = 253;

// MetaScan only ran when at least one AV engine reported: `total_avs === 0` is the platform's way of
// saying nothing scanned the file — an unsupported type, or a workflow that never included MetaScan
// (`current_av_result_a: "Not Scanned"`). Shared by the Overview card and the Download menu's
// Prevention Report entry so the two can never disagree: that report is generated from
// `scan_results`, so an unscanned file gives it nothing to render.
type PartialScanFields = Partial<Pick<ScanResults, 'total_avs' | 'scan_all_result_i'>>;

export const hasMetascanRun = (scanResults?: PartialScanFields): boolean => (scanResults?.total_avs ?? 0) > 0;

// With engines behind it, 253 is a partial scan, not a failed one. Every surface that decides "Failed"
// goes through this rule so one scan reads the same everywhere.
export const isMetascanPartial = (scanResults?: PartialScanFields): boolean =>
    hasMetascanRun(scanResults) && scanResults?.scan_all_result_i === EXCEEDED_LIMITS_CODE;

export const getMultiscanVerdict: GetMultiscanVerdict = (scanResults) => {
    // 22 (Potentially Unwanted Program) is a detection the platform leaves out of
    // total_detected_avs, so the aggregate code has to be checked too (MDCL-18093).
    if (
        (scanResults?.total_detected_avs ?? 0) > 0 ||
        scanResults?.scan_all_result_i === 1 ||
        scanResults?.scan_all_result_i === ScanResultCode.PotentiallyUnwanted
    ) {
        return { variant: 'alert', label: 'Detected', color: 'tw-text-red-600' };
    }

    if (
        scanResults?.scan_all_result_i === 3 ||
        scanResults?.scan_all_result_a === 'Aborted' ||
        (scanResults?.scan_all_result_i === EXCEEDED_LIMITS_CODE && !isMetascanPartial(scanResults))
    ) {
        return { variant: 'alert', label: 'Failed', color: 'tw-text-red-600' };
    }

    if (
        scanResults?.scan_all_result_i === 15 ||
        scanResults?.scan_all_result_i === 52 ||
        scanResults?.scan_all_result_i === 53 ||
        scanResults?.scan_all_result_i === 9 ||
        scanResults?.scan_all_result_i === 12 ||
        scanResults?.scan_all_result_i === 13 ||
        scanResults?.scan_all_result_i === 14 ||
        scanResults?.scan_all_result_i === 15 ||
        scanResults?.scan_all_result_i === 16 ||
        scanResults?.scan_all_result_i === 24
    ) {
        return { variant: 'alert', label: 'Failed', color: 'tw-text-yellow-500' };
    }

    return { variant: 'inactive', label: 'Not Detected', color: 'tw-text-green-600' };
};

export const verdictDLP: Record<string, VerdictObject> = {
    0: { variant: 'inactive', label: 'Not Detected' },
    1: { variant: 'alert', label: 'Detected' },
    2: { variant: 'alert', label: 'Detected' },
    3: { variant: 'alert', label: 'Failed' },
    4: { variant: 'inactive', label: 'Not Supported' },
    'Very Low': { variant: 'neutral' },
    Low: { variant: 'success', label: 'Low' },
    Medium: { variant: 'inProgress', label: 'Moderate' },
    High: { variant: 'info', label: 'High' },
    'Very High': { variant: 'alert', label: 'Very High' },
    'Not Detected': { variant: 'inactive' },
    false: { variant: 'alert', label: 'Not Redacted' },
    true: { variant: 'neutral', label: 'Redacted' },
    default: { variant: 'inactive', label: 'Not Active' },
    undefined: { variant: 'inactive', label: 'Not Active' },
};

export const statusMap = {
    Blocked: { variant: 'secure', type: 'status' },
    Allowed: { variant: 'neutral', type: 'status' },
    Failed: { variant: 'alert', type: 'status' },
    'In Progress': { variant: 'warn', type: 'status' },
};

// Deep CDR skipped the file rather than failing on it: the backend reports it as an Error with this
// reason. It reads Not Supported like DLP and AI Content Inspector do, not Blocked (MDCI-998).
export const isCdrUnsupported = (sanitized?: Sanitized): boolean =>
    sanitized?.result === 'Error' && sanitized.reason?.toLowerCase() === 'unsupported file type';

export const getSanitizedVerdict: GetSanitizedVerdict = (sanitized) => {
    const sanitizedVerdicts: Record<string, BadgeProps> = {
        sanitized: { label: 'Complete', variant: 'success' },
        unsupported: { label: 'Not Supported', variant: 'inactive' },
        error: { label: 'Blocked', variant: 'alert' },
        default: { label: 'Not Active', variant: 'inactive' },
    };

    if (sanitized?.file_path) {
        return sanitizedVerdicts.sanitized;
    }

    if (isCdrUnsupported(sanitized)) {
        return sanitizedVerdicts.unsupported;
    }

    if (sanitized?.result === 'Error') {
        return sanitizedVerdicts.error;
    }

    return sanitizedVerdicts.default;
};

export const verdictTagMapping: Record<string, VerdictTag> = {
    NO_THREAT: { verdict: 'No Threat', variant: 'inactive' },
    MALICIOUS: { verdict: 'Malicious', variant: 'alert' },
    LIKELY_MALICIOUS: { verdict: 'Likely Malicious', variant: 'alert' },
    SUSPICIOUS: { verdict: 'Suspicious', variant: 'inProgress' },
};

export const sortVerdictTags = (tags: TagWithVerdict[]) => {
    const tagOrder = ['NO_THREAT', 'MALICIOUS', 'LIKELY_MALICIOUS', 'SUSPICIOUS'];
    return [...tags].sort((tagFirst, tagSecond) => {
        const indexFirst = tagOrder.indexOf(tagFirst.tag.verdict.verdict);
        const indexSecond = tagOrder.indexOf(tagSecond.tag.verdict.verdict);
        return indexFirst - indexSecond;
    });
};

export const getSandboxVerdict: GetSandboxVerdict = (verdict) => {
    const sandboxVerdicts: Record<string, SandboxBadgeProps> = {
        SUSPICIOUS: { variant: 'alert', label: 'Detected' },
        MALICIOUS: { variant: 'alert', label: 'Detected' },
        LIKELY_MALICIOUS: { variant: 'alert', label: 'Detected' },
        UNKNOWN: { variant: 'inactive', label: 'Not Supported' },
        BENIGN: { variant: 'inactive', label: 'Not Detected' },
        NO_THREAT: { variant: 'inactive', label: 'Not Detected' },
        INFORMATIONAL: { variant: 'inactive', label: 'Not Detected' },
        error: { variant: 'alert', label: 'Failed' },
        undefined: { variant: 'inactive', label: 'Not Detected' },
        default: { variant: 'inactive', label: 'Not Detected' },
    };

    if (!verdict) {
        return sandboxVerdicts.default;
    }

    const sandboxVerdict = sandboxVerdicts?.[verdict] || sandboxVerdicts.NO_THREAT;

    return sandboxVerdict;
};

export const getStatusVerdict = (processResult?: string): StatusVerdict => {
    if (processResult === 'Allowed') {
        return { variant: 'allowed', label: 'Allowed' };
    }
    if (processResult === 'Blocked') {
        return { variant: 'blocked', label: 'Blocked' };
    }
    return { variant: 'neutral', label: 'Unknown' };
};

export const getScanVerdict: GetScanVerdict = (index) => {
    const verdicts: Record<number, BadgeProps> = {
        0: { variant: 'success', label: 'Not Detected' },
        1: { variant: 'alert', label: 'Detected' },
        2: { variant: 'alert', label: 'Detected' },
        3: { variant: 'alert', label: 'Failed' },
        9: { variant: 'alert', label: 'Detected' },
        10: { variant: 'alert', label: 'Failed' },
        12: { variant: 'alert', label: 'Detected' },
        13: { variant: 'alert', label: 'Detected' },
        14: { variant: 'alert', label: 'Detected' },
        15: { variant: 'alert', label: 'Detected' },
        16: { variant: 'alert', label: 'Detected' },
        22: { variant: 'alert', label: 'Detected' },
        23: { variant: 'inactive', label: 'Not Supported' },
        24: { variant: 'alert', label: 'Detected' },
        52: { variant: 'alert', label: 'Detected' },
        53: { variant: 'alert', label: 'Detected' },
    };

    if (Object.keys(verdicts).includes(index?.toString())) {
        return verdicts[index];
    }

    return verdicts[10];
};

type DlpCategory = { hits?: unknown[] };
type DlpInfo = { hits?: Record<string, DlpCategory | undefined>; verdict?: number };

export const getDlpVerdict = (dlpInfo: DlpInfo | null | undefined): VerdictObject => {
    // An explicit non-clean code wins (1 / 2 Detected, 3 Failed, 4 Not Supported — see verdictDLP
    // above). Code 0 is not a clean bill: it only says the hits stayed below the certainty threshold,
    // and the banner badge / Data Loss tab still count those rows, so the hit count decides there too
    // or the Overview card contradicts them on the same result (MDCI-1038).
    const coded = dlpInfo?.verdict ? verdictDLP[dlpInfo.verdict] : undefined;
    if (coded) {
        return coded;
    }

    const totalHits = Object.values(dlpInfo?.hits ?? {}).reduce((total: number, category: DlpCategory | undefined) => {
        return total + (category?.hits?.length ?? 0);
    }, 0);

    return totalHits > 0 ? verdictDLP['1'] : verdictDLP['0'];
};

// Maps internal verdict variants to the UI Verdict component's allowed variants
export const mapToVerdictVariant = (
    variant?: string,
): NonNullable<ComponentProps<typeof VerdictComponent>['variant']> => {
    switch (variant) {
        case 'alert':
            return 'alert';
        case 'neutral':
            return 'neutral';
        case 'success':
            return 'success';
        // Deliberately not the UI's `inactive`: its dot is invisible on dark and the row reads as a
        // disabled control. A verdict that did not run is neutral, not disabled.
        case 'inactive':
            return 'neutral';
        case 'info':
            return 'accent';
        case 'inProgress':
            return 'warn';
        case 'failed':
            return 'alert';
        case 'red':
            return 'alert';
        case 'orange':
            return 'caution';
        case 'yellow':
            return 'warn';
        case 'green':
            return 'success';
        default:
            return 'neutral';
    }
};

// Mapping function to convert verdict variants to UI component variants
export const mapVerdictToUIVariant = (
    verdictVariant: string,
): 'failed' | 'success' | 'neutral' | 'info' | 'inProgress' | 'inactive' | 'red' | 'yellow' | 'green' | 'orange' => {
    switch (verdictVariant) {
        case 'critical':
            return 'red';
        case 'neutral':
            return 'neutral';
        case 'high':
            return 'orange';
        case 'medium':
            return 'yellow';
        case 'low':
            return 'green';
        case 'none':
        case 'inProgress':
            return 'neutral';
        default:
            return 'neutral';
    }
};

interface SeverityVerdict {
    variant: NonNullable<ComponentProps<typeof Severity>['variant']>;
    label: string;
}

type GetSeverityVerdict = (severity?: string) => SeverityVerdict;

export const getSeverityVerdict: GetSeverityVerdict = (severity = 'default') => {
    const severityVerdicts: Record<string, SeverityVerdict> = {
        default: { variant: 'neutral', label: 'Unknown' },
        LOW: { variant: 'neutral', label: 'Low' },
        MODERATE: { variant: 'caution', label: 'Moderate' },
        IMPORTANT: { variant: 'warn', label: 'Important' },
        CRITICAL: { variant: 'alert', label: 'Critical' },
    };

    return severityVerdicts?.[severity] ?? severityVerdicts.default;
};

export const mapVerdictToSeverityType = (
    verdictVariant: string,
): 'malicious' | 'likelyMalicious' | 'suspicious' | 'noThreat' | 'benign' | 'unknown' => {
    switch (verdictVariant) {
        case 'critical':
            return 'malicious';
        case 'high':
            return 'malicious';
        case 'medium':
            return 'likelyMalicious';
        case 'low':
            return 'suspicious';
        case 'neutral':
            return 'noThreat';
        case 'none':
        default:
            return 'unknown';
    }
};
type ScanStatusVariant = 'allowed' | 'blocked' | 'failed' | 'skipped' | 'complete' | 'pending';

export const mapTagsSandbox = (verdict: string): ScanStatusVariant => {
    switch (verdict) {
        case 'NO_THREAT':
            return 'allowed';
        case 'UNKNOWN':
            return 'pending';
        case 'MALICIOUS':
            return 'blocked';
        case 'LIKELY_MALICIOUS':
            return 'failed';
        case 'SUSPICIOUS':
            return 'skipped';
        default:
            return 'complete';
    }
};

export const mapTagsSandboxToScanStatus = (verdict: string): { variant: ComponentProps<typeof Tag>['variant'] } => {
    switch (verdict) {
        case 'NO_THREAT':
            return { variant: 'success' };
        case 'UNKNOWN':
            return { variant: 'neutral' };
        case 'MALICIOUS':
            return { variant: 'alert' };
        case 'LIKELY_MALICIOUS':
            return { variant: 'alert' };
        case 'SUSPICIOUS':
            return { variant: 'neutral' };
        default:
            return { variant: 'success' };
    }
};

export const verdictUrl: Record<number, VerdictObject> = {
    0: { variant: 'success', label: 'Trustworthy' },
    1: { variant: 'alert', label: 'Detected' },
    2: { variant: 'warn', label: 'Suspicious' },
    5: { variant: 'success', label: 'No Threat' },
};

export const verdictSeverityMap: Record<string, { icon: ComponentProps<typeof Icon>['iconName']; className: string }> =
    {
        alert: {
            icon: 'signal-4',
            className: 'tw:bg-red-800 tw:dark:bg-red-400',
        },
        warn: {
            icon: 'signal-3',
            className: 'tw:bg-orange-700 tw:dark:bg-orange-700',
        },
        caution: {
            icon: 'signal-2',
            className: 'tw:bg-yellow-700 tw:dark:bg-yellow-600',
        },
        success: {
            icon: 'signal-1',
            className: 'tw:bg-green-800 tw:dark:bg-green-700',
        },
        neutral: {
            icon: 'signal-0',
            className: 'tw:bg-neutrals-600 tw:dark:bg-neutrals-600',
        },
    };

export const getVerdictSeverity = (verdict: string) => {
    switch (verdict) {
        case 'MALICIOUS':
            return 'alert';
        case 'LIKELY_MALICIOUS':
            return 'warn';
        case 'SUSPICIOUS':
            return 'caution';
        case 'NO_THREAT':
            return 'success';
        default:
            return 'neutral';
    }
};

interface ThreatLevelVerdict {
    label: string;
    variant: SeverityColorVariant;
}

/**
 * Maps threatLevel numeric values to human-friendly verdict labels and severity variants.
 * Based on the threat level mapping:
 * - 0–0.25 (inclusive), e.g. 0.1–0.25 → No Threat Detected (success)
 * - 0.5 → Low Risk (caution)
 * - 0.75 → High Risk (warn)
 * - 1 → Confirmed Threat (alert)
 * - -1 → Trusted (neutral)
 * - "D" or undefined → Not Active (neutral)
 * - "N/A" → System Error (neutral)
 */
export const getVerdictFromThreatLevel = (threatLevel?: number | string): ThreatLevelVerdict => {
    if (threatLevel === undefined || threatLevel === null) {
        return { label: 'Not Active', variant: 'neutral' };
    }

    if (threatLevel === 'N/A') {
        return { label: 'System Error', variant: 'neutral' };
    }

    const level = typeof threatLevel === 'string' ? Number.parseFloat(threatLevel) : threatLevel;

    if (Number.isNaN(level)) {
        return { label: 'Not Active', variant: 'neutral' };
    }

    if (level >= 0 && level <= 0.25) {
        return { label: 'No Threat Detected', variant: 'success' };
    }

    if (level === 0.5) {
        return { label: 'Low Risk', variant: 'caution' };
    }

    if (level === 0.75) {
        return { label: 'High Risk', variant: 'warn' };
    }

    if (level === 1) {
        return { label: 'Confirmed Threat', variant: 'alert' };
    }

    if (level === -1) {
        return { label: 'Trusted', variant: 'neutral' };
    }

    // Fallback for any other values
    return { label: 'Not Active', variant: 'neutral' };
};

export type SeverityColorVariant = 'neutral' | 'alert' | 'success' | 'caution' | 'warn';
export const mapToSeverityVariant = (variant?: string): SeverityColorVariant => {
    switch (variant) {
        case 'success':
        case 'green':
            return 'success';
        case 'failed':
        case 'red':
            return 'alert';
        case 'inProgress':
        case 'yellow':
            return 'caution';
        case 'inactive':
        case 'neutral':
            return 'neutral';
        case 'info':
        case 'orange':
            return 'warn';
        case 'alert':
            return 'alert';
        default:
            return 'neutral';
    }
};

export const getScanResultLabel = (resultCode: ScanResultCode) => {
    const scanResultLabels = {
        [ScanResultCode.NoThreatDetected]: 'No Threat Detected',
        [ScanResultCode.Infected]: 'Infected',
        [ScanResultCode.Suspicious]: 'Suspicious',
        [ScanResultCode.Failed]: 'Failed',
        [ScanResultCode.Whitelisted]: 'Allowlisted',
        [ScanResultCode.Blacklisted]: 'Blocklisted',
        [ScanResultCode.ExceededArchiveDepth]: 'Exceeded Archive Depth',
        [ScanResultCode.NotScanned]: 'Not Scanned',
        [ScanResultCode.EncryptedArchive]: 'Encrypted Archive',
        [ScanResultCode.ExceededArchiveSize]: 'Exceeded Archive Size',
        [ScanResultCode.ExceededArchiveFileNumber]: 'Exceeded Archive File Number',
        [ScanResultCode.PasswordProtectedDocument]: 'Password Protected Document',
        [ScanResultCode.ExceededArchiveTimeout]: 'Exceeded Archive Timeout',
        [ScanResultCode.Mismatch]: 'Mismatch',
        [ScanResultCode.PotentiallyVulnerableFile]: 'Potentially Vulnerable File',
        [ScanResultCode.Cancelled]: 'Cancelled',
        [ScanResultCode.SensitiveDataFound]: 'Sensitive Data Found',
        [ScanResultCode.YaraRuleMatched]: 'Yara Rule Matched',
        [ScanResultCode.PotentiallyUnwanted]: 'Potentially Unwanted',
        [ScanResultCode.UnsupportedFileType]: 'Unsupported File Type',
        [ScanResultCode.ExtractionFailed]: 'Extraction Failed',
        [ScanResultCode.ScanFailed]: 'Scan Failed',
        [ScanResultCode.SuspiciousSandbox]: 'Suspicious Verdict by Sandbox',
        [ScanResultCode.LikelyMaliciousSandbox]: 'Likely Malicious Verdict by Sandbox',
        [ScanResultCode.MaliciousSandbox]: 'Malicious Verdict by Sandbox',
        [ScanResultCode.BlockedSandbox]: 'Blocked Verdict by Sandbox',
        [ScanResultCode.BlockedDeepCDR]: 'Blocked Verdict by Deep CDR',
        [ScanResultCode.GlobalTimeoutExceeded]: 'Global Timeout Exceeded',
        [ScanResultCode.VulnerableSBOM]: 'Vulnerable Verdict by SBOM',
        [ScanResultCode.NonVulnerableSBOM]: 'Non-vulnerable Verdict by SBOM',
        [ScanResultCode.BlockedSBOM]: 'Blocked Verdict by SBOM',
        [ScanResultCode.WeakVulnerableSBOM]: 'Weak-vulnerable Verdict by SBOM',
        [ScanResultCode.BlockedByPostAction]: 'Blocked by Post Action',
        [ScanResultCode.KnownBad]: 'Known Bad',
        [ScanResultCode.KnownGood]: 'Known Good',
        [ScanResultCode.Unknown]: 'Unknown',
        [ScanResultCode.AllowedCOO]: 'Allowed Verdict by COO',
        [ScanResultCode.BlockedCOO]: 'Blocked Verdict by COO',
        [ScanResultCode.UnknownCOO]: 'Unknown Verdict by COO',
        [ScanResultCode.Exempted]: 'Exempted',
        [ScanResultCode.CompressionFailed]: 'Compression Failed',
        [ScanResultCode.InProgress]: 'In Progress',
        [ScanResultCode.SkipProcessingFastSymlink]: 'Skip Processing Fast Symlink',
    };

    return scanResultLabels[resultCode] ?? scanResultLabels[ScanResultCode.Unknown];
};

export const getAVResultLabel = (result: AVResult) => {
    const avResultLabels = {
        [AVResult.Clean]: 'Clean',
        [AVResult.Infected]: 'Infected',
        [AVResult.Suspicious]: 'Suspicious',
        [AVResult.FailedToScan]: 'Failed to Scan',
        [AVResult.NotScanned]: 'Not Scanned',
        [AVResult.PotentiallyUnwanted]: 'Potentially Unwanted',
    };

    return avResultLabels[result] ?? avResultLabels[AVResult.NotScanned];
};

export const getCDRResultLabel = (result: CDRResult) => {
    const cdrResultLabels = {
        [CDRResult.DSBlocked]: 'Blocked',
        [CDRResult.SanitizedOrPartially]: 'Sanitized or Partially',
        [CDRResult.SanitizationFailedOrTimedout]: 'Sanitization Failed or Timed Out',
        [CDRResult.NotSanitized]: 'Not Sanitized',
        [CDRResult.SanitizationSkipped]: 'Sanitization Skipped',
    };

    return cdrResultLabels[result] ?? cdrResultLabels[CDRResult.NotSanitized];
};
